/**
 * Single source of truth for "how long is this job listing valid for": reconciles the source's
 * own applicationDeadline/postedDate with how recently our crawler actually reconfirmed the ad
 * (lastSeen). Shared by get-job-listing-http-status-code.ts (410 vs 200), generate-job-posting-
 * schema.ts (JobPosting `validThrough`) and lambda/maintainer.ts (the isActive flag), so they
 * can't disagree: a job could otherwise render 200 while its schema already claimed it expired.
 *
 * 2026-09-10: reverted to trusting lastSeen directly for normally-crawled jobs (see
 * RECHECK_WINDOW_HOURS below), now that crawling runs daily again (DailyCrawlScheduleRule in
 * cdk/lib/vike-stack.ts). The applicationDeadline/postedDate+35-day scheme this replaces was
 * itself a 2026-09-04 fix for a much older flat lastSeen-staleness rule that had gone stale
 * from irregular crawl cadence; the deadline math is a smaller regression risk for jobs with no
 * crawler at all (see the isLinkedinImport branch below), so it's kept for those.
 *
 * 2026-09-12: default window widened 36h -> 72h as a stopgap — real crawl reliability for many
 * doCrawl=TRUE companies isn't actually daily-without-fail yet, see RECHECK_WINDOW_HOURS below.
 */
// Namespace import + unwrap: lambda/ has no "type": "module" (see server/utils/lambda-interop.ts).
import { unwrapLambdaModule } from '../../server/utils/lambda-interop';
import * as parseLastSeenNs from '../../lambda/utils/parse-last-seen-to-ms';
const { parseLastSeenToUnixMs } = unwrapLambdaModule(parseLastSeenNs);

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Hours since a normally-crawled job's lastSeen before its listing is presumed gone. lastSeen is
 * bumped on every successful (re-)crawl even when the extracted content is unchanged (see
 * update-job-last-seen-and-meta.ts), so this is really "missed roughly one and a half daily
 * crawls in a row." Override with MAINTAINER_RECHECK_HOURS (1-8760); not set anywhere in
 * .env/.env.production today, so every Lambda uses the default below unless that changes.
 *
 * 2026-09-12: bumped default 36h -> 72h. The 2026-09-10 revert to a flat lastSeen window assumed
 * every doCrawl=TRUE company gets re-crawled daily without fail; the harness triage snapshot
 * (harness/state/harness.db, 2026-08-26) shows that's not true in practice — 503 of 1,813
 * doCrawl=TRUE companies had a recentFailStreak>=4, and 354 were flat-out BROKEN/NEVER_WORKED.
 * A company that misses even one day's crawl was tipping its jobs over the old 36h cliff on the
 * very next maintainer.ts run, which is the leading suspect for a live active-job-count collapse
 * observed 2026-09-12. 72h buys roughly one full missed crawl day of slack while a fix for the
 * underlying crawl-reliability gap is worked out separately; this is a stopgap, not a fix for
 * that gap. See docs/knowledge-graph/03-crawling-data-pipeline.md.
 */
function readRecheckWindowHours(): number {
  const raw = (typeof process !== 'undefined' ? process.env.MAINTAINER_RECHECK_HOURS : undefined)?.trim();
  if (!raw) return 72;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return 72;
  return Math.min(n, 24 * 365);
}
export const RECHECK_WINDOW_HOURS = readRecheckWindowHours();

/**
 * Employer-managed (LinkedIn-imported hosted-careers-page) jobs have no crawler visiting them at
 * all: employerJobsRefresh.ts re-fetches each one once a day and deliberately does nothing on a
 * failed fetch (LinkedIn authwalls are common) rather than risk a false deactivation, trusting
 * this longer window as its backstop. RECHECK_WINDOW_HOURS above would flip these jobs inactive
 * after roughly one bad day, so they keep the older, more generous scheme. Also used as the
 * fallback below for the rare case any job's lastSeen itself is missing.
 */
export const NO_DEADLINE_VALID_DAYS = 35;
export const RECONFIRMED_GRACE_DAYS = 30;

export interface JobListingValidityInput {
  postedDate?: string | number;
  lastSeen?: string | number;
  applicationDeadline?: string | number;
  /** See the employer-managed carve-out above. Only reliably populated where the caller reads
   * straight from FinalJobs (e.g. lambda/maintainer.ts); Typesense-sourced job objects (the JDP
   * page, search-results schema) don't index this field yet, so they fall through to the
   * RECHECK_WINDOW_HOURS branch for these jobs too until that's added. */
  isLinkedinImport?: boolean;
}

function toEpochMs(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null) return undefined;
  const ms = typeof value === 'string' ? Date.parse(value) : value;
  return typeof ms === 'number' && !isNaN(ms) ? ms : undefined;
}

/**
 * applicationDeadline if given, else NO_DEADLINE_VALID_DAYS after postedDate; if that's already
 * passed but lastSeen shows the ad was reconfirmed after that date, extend RECONFIRMED_GRACE_DAYS
 * past that sighting instead of trusting the stale date. Used for employer-managed jobs' own
 * expiry decision below, and by generate-job-posting-schema.ts to compute a publishable
 * `validThrough` when the source actually gave a real applicationDeadline: what to tell Google
 * this job's real closing date is is a different question from whether we currently trust the
 * listing enough to keep serving it (RECHECK_WINDOW_HOURS below), and coupling the two would mean
 * publishing an obviously wrong date whenever they disagree (either far too soon, when the real
 * deadline is genuinely weeks out and lastSeen is merely tight-windowed, or already in the past,
 * when a stale deadline has since been reconfirmed live).
 */
export function computeDeadlineAwareValidThroughMs(job: JobListingValidityInput, nowMs: number): number {
  const lastSeenMs = parseLastSeenToUnixMs(job.lastSeen);
  const deadlineMs = toEpochMs(job.applicationDeadline);
  const postedMs = toEpochMs(job.postedDate);
  const noDeadlineFallbackMs = NO_DEADLINE_VALID_DAYS * DAY_MS;

  const baseMs =
    deadlineMs ??
    (postedMs !== undefined
      ? postedMs + noDeadlineFallbackMs
      : (lastSeenMs ?? nowMs) + noDeadlineFallbackMs);
  if (lastSeenMs !== undefined && lastSeenMs >= baseMs) {
    return lastSeenMs + RECONFIRMED_GRACE_DAYS * DAY_MS;
  }
  return baseMs;
}

/**
 * The timestamp (ms) through which `job` is considered a valid, live listing (drives isActive /
 * the 410-vs-200 decision; NOT what generate-job-posting-schema.ts should publish as `validThrough`
 * when a real deadline is known, see computeDeadlineAwareValidThroughMs above for that).
 *
 * - Employer-managed jobs (isLinkedinImport): computeDeadlineAwareValidThroughMs, unchanged from
 *   before 2026-09-10 (see its docstring). They have no crawler visiting them, so lastSeen freshness
 *   alone isn't a trustworthy signal.
 * - Everything else: lastSeen + RECHECK_WINDOW_HOURS, full stop. applicationDeadline/postedDate
 *   are not consulted here even when present; a job not reconfirmed within one recheck window is
 *   presumed gone regardless of what its own stated deadline says.
 * - No lastSeen at all should be rare (every write path sets it): falls back to
 *   computeDeadlineAwareValidThroughMs so a row missing it isn't treated as already-expired.
 */
export function computeJobListingValidThroughMs(job: JobListingValidityInput, nowMs: number): number {
  if (job.isLinkedinImport === true) {
    return computeDeadlineAwareValidThroughMs(job, nowMs);
  }

  const lastSeenMs = parseLastSeenToUnixMs(job.lastSeen);
  if (lastSeenMs !== undefined) {
    return lastSeenMs + RECHECK_WINDOW_HOURS * HOUR_MS;
  }
  return computeDeadlineAwareValidThroughMs(job, nowMs);
}
